import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { UsersService } from '../../@services/users.service';
import { ToastService } from '../../@services/toast.service';

/**
 * Super-admin queue for user-deletion requests filed by client_admin
 * users. Each pending row carries the requester audit (who requested,
 * which role, when, optional reason) plus the target user's basic
 * attrs (email, customers, role) so the approver can see context
 * without an extra fetch.
 *
 * Approve → actual Cognito AdminDeleteUser + Mongo cleanup. Reject →
 * clears the pending flag (with optional reason captured for audit).
 * Both routes are super-admin-only (AdminAuthGuard); this component
 * mounts on `/admin/pending-deletions`.
 *
 * Part of the Option Pit customer-host migration's client_admin slice
 * (project_option_pit_customer_host_migration). Generic across all
 * client_admins — not OP-specific.
 */
@Component({
  selector: 'app-pending-deletions',
  imports: [CommonModule, FormsModule],
  templateUrl: './pending-deletions.component.html',
  styleUrl: './pending-deletions.component.scss',
})
export class PendingDeletionsComponent implements OnInit {
  loading = false;
  rows: any[] = [];
  rejectReason: { [username: string]: string } = {};

  constructor(private usersService: UsersService, private toast: ToastService) {}

  async ngOnInit() {
    await this.refresh();
  }

  async refresh() {
    this.loading = true;
    const res = await this.usersService.getPendingDeletions();
    this.loading = false;
    if (res && !res.err) {
      this.rows = res;
    } else {
      this.rows = [];
    }
  }

  fmtDate(d: any): string {
    if (!d) return '';
    return DateTime.fromJSDate(new Date(d)).setZone('America/New_York').toFormat('LLL d yyyy, HH:mm');
  }

  async approve(row: any) {
    const res = await this.usersService.approveUserDeletion(row.username);
    if (res?.success) {
      this.toast.success('User deleted', row.username);
      this.rows = this.rows.filter((r) => r.username !== row.username);
    }
  }

  async reject(row: any) {
    const reason = this.rejectReason[row.username] || '';
    const res = await this.usersService.rejectUserDeletion(row.username, reason);
    if (res?.success) {
      this.toast.info('Deletion rejected', row.username);
      this.rows = this.rows.filter((r) => r.username !== row.username);
      delete this.rejectReason[row.username];
    }
  }
}
